// Role display metadata — UI-side companion to packages/shared/labels.
// Labels themselves live in `@nexus/shared/labels` ROLE_LABELS so
// AI tool_result enrichment + frontend UI + AI Usage page all read
// from the same source (drift caused「單位管理員」 vs「管理員」 split
// bug pre-2026-05). This module adds the UI-specific extras —
// scope grouping for invite pickers + descriptions shown in form
// callouts — that don't belong in the cross-cutting label map.
//
// Naming convention: TENANT-LEVEL roles carry「單位」 prefix so the
// operator can't confuse them with site-scoped roles. SITE-SCOPED roles
// say「指派案場」 in their description and behave as you'd expect when
// you assign them to a site.
//
// Wrong-impression bug fix history: 「管理員」 = tenant_admin (tenant-
// wide) but operators kept misreading it as「site manager」 because
// the surrounding UX put them on a site detail page. Renamed labels +
// added scope grouping + warning callout to stop the misread.

import { ROLE_LABELS, roleLabel as sharedRoleLabel } from "@nexus/shared/labels";

export type RoleScopeKind = "tenant" | "site" | "individual";

export interface RoleMeta {
  /** Display label — comes from the shared label map so AI
   *  tool_result enrichment and the Users page render the same. */
  label: string;
  /** Scope grouping for the picker's optgroup + warning copy. */
  scope: RoleScopeKind;
  /** One-paragraph plain description shown under the picker after
   *  selection. Should NOT use jargon — operators speak the domain. */
  description: string;
}

// Scope + description live here (UI-only extras); the label string is
// pulled from ROLE_LABELS so any future label rename only touches the
// shared map. Roles present in ROLE_LABELS but not in ROLE_META —
// e.g. control_center_operator / client_viewer / super_admin — exist
// for AI / audit labelling but don't surface in the invite picker yet.
export const ROLE_META: Record<string, RoleMeta> = {
  // ── 整個單位（tenant-wide）───────────────────────────────
  tenant_admin: {
    label: ROLE_LABELS.tenant_admin!,
    scope: "tenant",
    description: "整個單位的最高權限。可管理任何案場、使用者、計費、邀請碼。無法限定單一案場。",
  },
  manager: {
    label: ROLE_LABELS.manager!,
    scope: "tenant",
    description: "整個單位的營運主管。可管理任何案場的排班、巡邏、任務、事件。無法限定單一案場。",
  },
  auditor: {
    label: ROLE_LABELS.auditor!,
    scope: "tenant",
    description: "整個單位的唯讀稽核權限。可查閱任何案場的資料與操作紀錄，但不能修改。",
  },

  // ── 指派案場（site-scoped）──────────────────────────────
  site_supervisor: {
    label: ROLE_LABELS.site_supervisor!,
    scope: "site",
    description: "限定在指派的案場：可派班、處理事件、看當案場的報表與用戶。無法跨案場。",
  },
  officer: {
    label: ROLE_LABELS.officer!,
    scope: "site",
    description: "限定在指派的案場執勤：掃地點、回報異常、看自己的班表與紀錄。",
  },

  // ── 獨立 / 其他 ──────────────────────────────────────
  incident_reporter: {
    label: ROLE_LABELS.incident_reporter!,
    scope: "individual",
    description: "獨立通報員。僅能通報異常事件，沒有其他資料存取權限。",
  },
};

/** Plain label lookup — re-export of the shared module so existing
 *  callers don't have to learn a new import path. */
export const roleLabel = sharedRoleLabel;

export const ROLE_GROUPS: Array<{
  kind: RoleScopeKind;
  title: string;
  hint: string;
  roles: string[];
}> = [
  {
    kind: "tenant",
    title: "整個單位",
    hint: "跨所有案場的權限。無法限定單一案場。",
    roles: ["tenant_admin", "manager", "auditor"],
  },
  {
    kind: "site",
    title: "指派案場",
    hint: "限定在你指派的案場內活動。",
    roles: ["site_supervisor", "officer"],
  },
  {
    kind: "individual",
    title: "其他",
    hint: "獨立功能身分。",
    roles: ["incident_reporter"],
  },
];
